import { extension as mimeToExtension, types as mimePorExtensao } from "mime-types";

/**
 * Extensão → MIME pelo MAPA do pacote, nunca por `lookup()`.
 *
 * `mime-types.lookup()` faz `require('path').extname` na linha 16 do pacote. No
 * navegador o webpack 5 não entrega o módulo `path`, `extname` chega `undefined` e a
 * chamada estoura `TypeError` — foi o que derrubou o widget em 28/07/2026. O mapa
 * `types` traz os mesmos dados e é objeto puro, sem dependência de Node.
 *
 * `mimeToExtension` (o caminho inverso) é seguro e já rodava em produção antes do
 * incidente. Regressão coberta em `fileTypesBrowser.test.ts`.
 */
function extensionToMime(ext: string): string | false {
    return mimePorExtensao[ext] ?? false;
}

/**
 * Decide o tipo de um anexo pela EXTENSÃO DO NOME, com o MIME como desempate.
 *
 * A validação antiga partia só do MIME informado pelo aparelho
 * (`acceptedExtensions.includes(mimeToExtension(file.type))`) e recusava casos que o
 * Android produz o tempo todo: `image/jpg` (fora do padrão → a lib devolve `false`),
 * tipo vazio e `application/octet-stream` de provedores que não declaram nada. O
 * resultado prático era o cliente não conseguir mandar o print que o atendente pediu.
 *
 * O nome do arquivo é o dado mais estável nesse cenário — é o que o cliente vê e o
 * que o sistema preserva mesmo quando erra o MIME. O MIME cobre o caminho inverso:
 * imagem colada da área de transferência, que chega sem nome útil.
 *
 * ⚠️ Filtro de EXPERIÊNCIA, não de segurança: roda no navegador e a mídia sobe direto
 * para o Twilio, sem backend nosso no meio para conferir magic-bytes. Serve para
 * evitar erro honesto, não para barrar quem quer burlar.
 */

/** Extensão de 1 a 5 caracteres alfanuméricos no fim do nome. */
const EXTENSAO_NO_NOME = /\.([a-z0-9]{1,5})$/i;

/** Extensão normalizada (minúscula, sem ponto) do arquivo; "" quando indeterminável. */
export function extensionOf(fileName: string, mimeType: string): string {
    const doNome = EXTENSAO_NO_NOME.exec((fileName ?? "").trim());
    // Só vale como extensão se for um tipo CONHECIDO: nomes com data ("...23.09.13")
    // ou versão ("relatorio.v2") casariam o padrão e virariam extensão inventada.
    if (doNome && extensionToMime(doNome[1].toLowerCase())) {
        return doNome[1].toLowerCase();
    }
    const doMime = mimeToExtension((mimeType ?? "").toLowerCase());
    return typeof doMime === "string" ? doMime.toLowerCase() : "";
}

/**
 * O arquivo está entre os tipos permitidos?
 *
 * `undefined` = a marca não configurou restrição; não cabe ao widget inventar uma.
 * `[]` = decisão explícita de não aceitar anexo — recusa tudo. Tratar os dois como
 * "aceita tudo" faria quem zera a lista destravar `.exe`/`.html` achando o contrário
 * (era o comportamento do código antigo, e a inversão iria para o lado perigoso).
 */
export function isAcceptedFile(file: Pick<File, "name" | "type">, accepted?: string[]): boolean {
    if (accepted === undefined) {
        return true;
    }
    if (accepted.length === 0) {
        return false;
    }
    const permitidas = accepted.map((e) => e.toLowerCase().replace(/^\./, ""));
    const ext = extensionOf(file.name, file.type);
    return ext !== "" && permitidas.includes(ext);
}

/**
 * Valor do `accept` do input de arquivo: extensões E tipos MIME.
 *
 * Só com extensões o seletor do Chrome no Android fica instável — provedores como o
 * Google Fotos não expõem extensão, e imagens válidas aparecem esmaecidas ou nem
 * aparecem. Declarar o MIME junto é o que faz o seletor reconhecê-las.
 */
export function buildAcceptAttribute(accepted?: string[]): string {
    if (!accepted || accepted.length === 0) {
        return "";
    }
    const extensoes = accepted.map((e) => `.${e.toLowerCase().replace(/^\./, "")}`);
    const mimes: string[] = [];
    for (const ext of accepted) {
        const mime = extensionToMime(ext.toLowerCase().replace(/^\./, ""));
        if (typeof mime === "string" && !mimes.includes(mime)) {
            mimes.push(mime);
        }
    }
    return [...extensoes, ...mimes].join(",");
}
